import { BackButton } from 'components/common/BackButton'
import * as api from 'api/entityApi'
import { ErrorState, PageLoader } from 'components/common/Loader'
import { CallLogDetailView } from 'components/view/CallLogDetailView'
import { FollowUpDetailView } from 'components/view/FollowUpDetailView'
import { PhysicalAppointmentView } from 'components/view/PhysicalAppointmentView'
import { TaskDetailView } from 'components/view/TaskDetailView'
import { useAsyncData } from 'hooks/useAsyncData'
import type { EntityType } from 'types/entity'

/**
 * The non-lead branches of `jsEntityObj.CreateEntity`.
 *
 * These render a standalone detail card rather than the profile/tabs layout,
 * exactly as the legacy `CreateTaskView` / `CreateAppointmentView` /
 * `CreateFollowUpView` / `CreatePysAppointmentView` / `CreateCallLogView`
 * templates did.
 */
export default function SecondaryEntityView({
  entityType,
  entityId,
}: {
  entityType: EntityType
  entityId: string
}) {
  const isTaskLike = entityType === 'task' || entityType === 'appointment'

  const task = useAsyncData((signal) => api.fetchTaskDetail(entityId, signal), [entityId], {
    enabled: isTaskLike,
  })

  // The task view resolves CreatedBy through this lookup.
  const hashedUsers = useAsyncData((signal) => api.fetchUserFromHashedList(signal), [], {
    enabled: isTaskLike,
  })

  const followUps = useAsyncData((signal) => api.fetchInteractionHistories(entityId, signal), [entityId], {
    enabled: entityType === 'followup',
  })

  const phyAppointment = useAsyncData(
    (signal) => api.fetchPhysicalAppointment(entityId, signal),
    [entityId],
    { enabled: entityType === 'physicalappointment' },
  )

  const callLog = useAsyncData((signal) => api.fetchCallLogDetail(entityId, signal), [entityId], {
    enabled: entityType === 'calllogdetail',
  })

  const active = isTaskLike
    ? task
    : entityType === 'followup'
      ? followUps
      : entityType === 'physicalappointment'
        ? phyAppointment
        : callLog

  const title = TITLES[entityType] ?? 'Entity View Details'

  if (active.loading) return <PageLoader />

  return (
    <div className="app-shell">
      <header className="page-header">
        <div>
          <h1>{title}</h1>
          <div className="crumbs">
            {entityType} · {entityId}
          </div>
        </div>
        <div className="page-actions">
          <BackButton />
        </div>
      </header>

      {active.error ? (
        <ErrorState message={active.error} onRetry={active.reload} />
      ) : isTaskLike ? (
        task.data ? (
          <TaskDetailView
            task={task.data}
            taskId={entityId}
            hashedUsers={hashedUsers.data ?? []}
            heading={entityType === 'task' ? 'Task Details' : 'Appointment Details'}
          />
        ) : (
          <ErrorState message="Record not found." />
        )
      ) : entityType === 'followup' ? (
        <FollowUpDetailView interactions={followUps.data ?? []} />
      ) : entityType === 'physicalappointment' ? (
        phyAppointment.data ? (
          <PhysicalAppointmentView appointment={phyAppointment.data} appointmentId={entityId} />
        ) : (
          <ErrorState message="Record not found." />
        )
      ) : callLog.data ? (
        <CallLogDetailView callLog={callLog.data} />
      ) : (
        <ErrorState message="Record not found." />
      )}
    </div>
  )
}

const TITLES: Partial<Record<EntityType, string>> = {
  task: 'Task Details',
  appointment: 'Appointment Details',
  followup: 'Followup Details',
  physicalappointment: 'Appointment Details',
  calllogdetail: 'Call Log Details',
}
